import React, { memo } from 'react';
import './QuizCheckpoint.css';
import { Check, X, Minus } from 'lucide-react';

/**
 * @param {number} currentIndex - Index of the current active question (0-based)
 * @param {number} questionsCount - Total number of questions
 * @param {Array<'correct' | 'wrong' | 'skipped' | null>} statuses - Array tracking status per question index
 * @param {Function} [onSelectQuestion] - Optional callback if you want dots to be clickable
 */
const QuizCheckpoint = ({
  currentIndex,
  questionsCount,
  statuses = [],
  onSelectQuestion,
}) => {
  const total = questionsCount || statuses.length;
  if (!total || total <= 0) return null;

  const renderIconOrNumber = (status, idx, isCurrent) => {
    switch (status) {
      case 'correct':
        return <Check className="checkpoint-icon icon-correct" strokeWidth={3} size={18} aria-hidden="true" />;
      case 'wrong':
        return <X className="checkpoint-icon icon-wrong" strokeWidth={3} size={18} aria-hidden="true" />;
      case 'skipped':
        // Options for skipped:
        // 1. FastForward: implies "skipped ahead"
        // 2. CornerUpRight: jump/skip arrow
        // 3. Minus: clean, neutral "unanswered/skipped dash"
        return <Minus className="checkpoint-icon icon-skipped" strokeWidth={2.5} size={16} aria-hidden="true" />;
      default:
        return <span className="checkpoint-number">{idx + 1}</span>;
    }
  };

  const getAriaLabel = (status, idx, isCurrent) => {
    let stateDesc = 'not attempted';
    if (isCurrent) stateDesc = 'current question';
    else if (status === 'correct') stateDesc = 'answered correctly';
    else if (status === 'wrong') stateDesc = 'answered incorrectly';
    else if (status === 'skipped') stateDesc = 'skipped';

    return `Question ${idx + 1}: ${stateDesc}`;
  };

  return (
    <nav className="checkpoint-container" aria-label="Question Progress">
      <div className="checkpoint-row">
        {Array.from({ length: total }, (_, idx) => {
          const status = statuses[idx]; // 'correct' | 'wrong' | 'skipped' | undefined
          const isCurrent = idx === currentIndex;
          const isInteractive = Boolean(onSelectQuestion);

          return (
            <button
              type="button"
              key={idx}
              disabled={!isInteractive}
              onClick={() => isInteractive && onSelectQuestion(idx)}
              className={`checkpoint-dot ${status || 'unvisited'} ${isCurrent ? 'active' : ''} ${isInteractive ? 'interactive' : ''
                }`}
              aria-label={getAriaLabel(status, idx, isCurrent)}
              aria-current={isCurrent ? 'step' : undefined}
            >
              {renderIconOrNumber(status, idx, isCurrent)}
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default memo(QuizCheckpoint);